import { forwardRef, Suspense, useEffect, useRef, useState } from "react";
import { Divider, IconButton } from "@mui/material";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  ArrowUp,
  Calculator,
  Calendar,
  CreditCard,
  FileText,
  Info,
  LineChart,
  Search as SearchIcon,
  Settings,
  Slash,
  Smile,
  Trash2,
  User,
  X,
} from "lucide-react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { QueryContact, QuerySecurity } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { AppSidebar } from "@/components/app-sidebar";
import { Button } from "@/components/button";
import { CapguardPoll } from "@/components/CapguardPoll/CapguardPoll";
import {
  DialogTitle,
  Dialog,
  DialogContent,
  DialogDescription,
} from "@/components/dialog";
import { InputWithDebounce } from "@/components/input-with-debounce";
import { Loader } from "@/components/loader";
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import { MarketComment } from "@/components/MarketComment/MarketComment";
import MeetingRoomAvailabilityV3 from "@/components/MeetingRooms/MeetingRoomAvailability_v3";
import { NewContact } from "@/components/new-contact";
import { PremierLeague } from "@/components/PremierLeague/PremierLeague";
import Pricelist from "@/components/Pricelist/Pricelist";
import { Search as Searcher } from "@/components/Search/Search";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { ThemeSwitcher } from "@/components/theme-switcher";
import { Toaster } from "@/components/toaster";
import { useLocalStorageStore } from "@/hooks/useLocalStorageStore";
import { cn } from "@/lib/utils";
import { useKeycloak } from "@/providers/KeycloakProvider";

export default function Layout() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SidebarProvider>
      <Toaster />
      <AppSidebar />
      <SidebarInset>
        <header className="flex z-50 sticky bg-inherit top-0 h-14 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex justify-between items-center flex-1 gap-4">
            <div className="flex gap-2 items-center">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Searcher />
            </div>
            <div className="flex justify-start items-center flex-1">
              <CapguardPoll />
            </div>
            <div className="flex justify-end items-center gap-2">
              {/* <DocProducerButton variant="outline" /> */}
              <Pricelist />
              <MarketComment />
              <MeetingRoomAvailabilityV3 asButton />
              <NewContact asButton />
              <PremierLeague />
              <Separator orientation="vertical" className="h-4 mx-2" />

              <ThemeSwitcher />
            </div>
          </div>
        </header>
        <main className="flex flex-1 flex-col gap-4 p-4">
          <Suspense
            fallback={
              <div className="flex flex-1 flex-col gap-4 p-4">
                <div className="flex-1 flex justify-center items-center rounded-xl bg-muted/50 min-h-min min-w-min">
                  <LogoAnimation size={48} variant="spin" />
                </div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>

        {/* Scroll to top button */}
        <button
          onClick={scrollToTop}
          className={cn(
            "fixed bottom-1 right-1 z-50 size-6 rounded-full bg-primary text-primary-foreground shadow-lg",
            "flex items-center justify-center",
            "transition-all duration-300 ease-in-out",
            "hover:bg-primary/90 hover:scale-110 active:scale-95",
            showScrollTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4 pointer-events-none"
          )}
          aria-label="Scroll to top"
        >
          <ArrowUp className="size-3.5" />
        </button>
      </SidebarInset>
    </SidebarProvider>
  );
}

const CONTACT_SHORTCUTS = [
  { label: "Overview", tab: "overview" },
  { label: "DocProducer", tab: "docproducer" },
  { label: "Transactions", tab: "transactions" },
];

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for managing search history
 */
function useSearchHistoryState<T>(
  storageKey: string,
  getHistory: () => T[],
  deleteHistory: () => void,
  removeItem: (id: number) => void
) {
  const [history, setHistory] = useState<T[]>([]);

  const loadHistory = () => setHistory(getHistory());
  const clearHistory = () => {
    deleteHistory();
    setHistory([]);
  };
  const removeHistoryItem = (id: number) => {
    removeItem(id);
    setHistory((prev) =>
      prev.filter((item) => (item as unknown as { id: number }).id !== id)
    );
  };

  return { history, loadHistory, clearHistory, removeHistoryItem };
}

/**
 * Hook for filtering and displaying items
 */
function useFilteredItems<T extends { name: string }>(
  items: T[] | undefined,
  search: string,
  searchFields: (item: T) => string[],
  maxResults = 50,
  defaultLimit = 10
) {
  const isSearching = search.length > 0;

  const filtered = items
    ?.filter((item) => {
      const searchLower = search.toLowerCase();
      return searchFields(item).some((field) =>
        field.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, maxResults);

  const display = isSearching ? filtered : items?.slice(0, defaultLimit);

  return {
    items: display,
    isSearching,
    hasResults: (display?.length ?? 0) > 0,
  };
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

/**
 * Generic empty state component
 */
const EmptyState = ({ message }: { message: string }) => (
  <div className="w-full py-12 flex flex-col items-center justify-center gap-3">
    <div className="rounded-full bg-muted/50 p-4">
      <Info className="size-6 text-muted-foreground/70" />
    </div>
    <div className="text-muted-foreground text-sm font-medium">{message}</div>
  </div>
);

/**
 * Generic section header
 */
const SectionHeader = ({ label }: { label: string }) => (
  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wider px-3 py-2 bg-muted/30">
    {label}
  </div>
);

/**
 * Generic column header with optional action button
 */
const ColumnHeader = ({
  title,
  onAction,
  showAction,
}: {
  title: string;
  onAction?: () => void;
  showAction?: boolean;
}) => (
  <div className="flex justify-between items-center pb-3 px-1">
    <div className="text-foreground text-base font-semibold flex items-center gap-2">
      {title}
    </div>
    {showAction && onAction && (
      <Button
        size="sm"
        variant="ghost"
        onClick={onAction}
        className="h-7 px-2 text-muted-foreground hover:text-foreground"
      >
        <Trash2 className="size-3.5 mr-1" />
        <span className="text-xs">Slett historikk</span>
      </Button>
    )}
  </div>
);

/**
 * Generic loading state
 */
const LoadingState = () => (
  <div className="w-full h-full py-12 flex items-center justify-center">
    <LogoAnimation size={24} variant="spin" />
  </div>
);

// ============================================================================
// COLUMN COMPONENTS
// ============================================================================

interface ContactColumnProps {
  search: string;
  loading: boolean;
  contacts: QueryContact[] | undefined;
  history: SearchItem[];
  onClearHistory: () => void;
  onRemoveHistoryItem: (id: number) => void;
  onContactClick: (contact: QueryContact | SearchItem) => void;
  onClose: () => void;
}

const ContactColumn = ({
  search,
  loading,
  contacts,
  history,
  onClearHistory,
  onRemoveHistoryItem,
  onContactClick,
  onClose,
}: ContactColumnProps) => {
  const { items, isSearching, hasResults } = useFilteredItems(
    contacts,
    search,
    (contact) => [contact.name, contact.contactId]
  );

  const hasHistory = history.length > 0;
  const showHistory = hasHistory && !isSearching;

  return (
    <div className="flex flex-col h-full">
      <ColumnHeader
        title="Kontakter"
        onAction={onClearHistory}
        showAction={showHistory}
      />

      <div className="flex flex-col overflow-y-auto pr-3 space-y-1">
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {/* History */}
            {showHistory && (
              <div className="space-y-1">
                <SectionHeader label="Historikk" />
                <div className="space-y-0.5">
                  {history.map((contact) => (
                    <ContactRow
                      key={`history-${contact.id}`}
                      contact={contact}
                      onRemoveClick={() => onRemoveHistoryItem(contact.id)}
                      close={onClose}
                      onClick={() => onContactClick(contact)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {hasResults && (
              <div className="space-y-1">
                <SectionHeader
                  label={
                    isSearching
                      ? `Søkeresultater (${items?.length ?? 0})`
                      : "Kontakter"
                  }
                />
                <div className="space-y-0.5">
                  {items?.map((contact) => (
                    <ContactRow
                      key={`contact-${contact.id}`}
                      contact={contact}
                      close={onClose}
                      onClick={() => onContactClick(contact)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {isSearching && !hasResults && (
              <EmptyState message="Ingen kontakter funnet" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface SecurityColumnProps {
  search: string;
  loading: boolean;
  securities: QuerySecurity[] | undefined;
  history: SecuritySearchItem[];
  onClearHistory: () => void;
  onRemoveHistoryItem: (id: number) => void;
  onSecurityClick: (security: QuerySecurity | SecuritySearchItem) => void;
  onClose: () => void;
}

const SecurityColumn = ({
  search,
  loading,
  securities,
  history,
  onClearHistory,
  onRemoveHistoryItem,
  onSecurityClick,
  onClose,
}: SecurityColumnProps) => {
  const { items, isSearching, hasResults } = useFilteredItems(
    securities,
    search,
    (security) => [security.name, security.isin_code, security.security_code]
  );

  const hasHistory = history.length > 0;
  const showHistory = hasHistory && !isSearching;

  return (
    <div className="flex flex-col h-full">
      <ColumnHeader
        title="Verdipapirer"
        onAction={onClearHistory}
        showAction={showHistory}
      />

      <div className="flex flex-col overflow-y-auto pl-3 space-y-1">
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {/* History */}
            {showHistory && (
              <div className="space-y-1">
                <SectionHeader label="Historikk" />
                <div className="space-y-0.5">
                  {history.map((security) => (
                    <SecurityRow
                      key={`history-${security.id}`}
                      security={security}
                      onRemoveClick={() => onRemoveHistoryItem(security.id)}
                      close={onClose}
                      onClick={() => onSecurityClick(security)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {hasResults && (
              <div className="space-y-1">
                <SectionHeader
                  label={
                    isSearching
                      ? `Søkeresultater (${items?.length ?? 0})`
                      : "Verdipapirer"
                  }
                />
                <div className="space-y-0.5">
                  {items?.map((security) => (
                    <SecurityRow
                      key={`security-${security.id}`}
                      security={security}
                      close={onClose}
                      onClick={() => onSecurityClick(security)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {isSearching && !hasResults && (
              <EmptyState message="Ingen verdipapirer funnet" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN SEARCH COMPONENT
// ============================================================================

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openContacts, setOpenContacts] = useLocalStorageStore();
  const { linkedContact, access } = useKeycloak();

  // UI State
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Search History
  const contactHistory = useSearchHistoryState<SearchItem>(
    "searchHistory",
    getSearchHistory,
    deleteSearchHistory,
    removeSearchHistoryItem
  );

  const securityHistory = useSearchHistoryState<SecuritySearchItem>(
    "securitySearchHistory",
    getSecuritySearchHistory,
    deleteSecuritySearchHistory,
    removeSecuritySearchHistoryItem
  );

  // Data Fetching
  const { data: contacts, loading: contactsLoading } =
    useGetSavedQuery2<QueryContact>("advPortalContactsSimple", {
      limit: 3000,
      contactName: "%",
      advisorId:
        access.isAdmin || access.isBackoffice ? "admin" : linkedContact || "",
    });

  const { data: securities, loading: securitiesLoading } =
    useGetSavedQuery2<QuerySecurity>("advPortalInstrumentsSimple", {});

  // Effects
  useEffect(() => {
    if (open) {
      contactHistory.loadHistory();
      securityHistory.loadHistory();
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && !event.ctrlKey) {
        setOpen((prev) => !prev);
        event.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handlers
  const addContactToRecent = (contact: { id: number; name: string }) => {
    const exists = openContacts.some((c) => c.id === contact.id);
    if (exists) {
      const filtered = openContacts.filter((c) => c.id !== contact.id);
      setOpenContacts([contact, ...filtered]);
    } else {
      setOpenContacts([...openContacts, contact]);
    }
  };

  const handleContactClick = (contact: QueryContact | SearchItem) => {
    const contactData = {
      name: contact.name,
      id: contact.id,
      contactId: "contactId" in contact ? contact.contactId : "",
    };

    saveSearch(contactData);
    addContactToRecent({ id: contact.id, name: contact.name });
    setOpen(false);
    navigate(`/contacts/${contact.id}`);
  };

  const handleSecurityClick = (
    security: QuerySecurity | SecuritySearchItem
  ) => {
    const securityData = {
      name: security.name,
      id: security.id,
      isin_code: security.isin_code,
    };

    saveSecuritySearch(securityData);
    setOpen(false);
    navigate(`/securities/${security.id}`);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
        className="bg-muted/50"
      >
        <div className="flex justify-start gap-4 items-center w-52 text-muted-foreground">
          <SearchIcon className="size-5" />
          <div className="flex items-center gap-2">
            Type
            <div className="border rounded-md size-6 flex items-center justify-center">
              /
            </div>
            to search
          </div>
        </div>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-4 overflow-hidden max-w-4xl">
          <VisuallyHidden.Root>
            <DialogTitle>Søk</DialogTitle>
            <DialogDescription>
              Søk etter kontakter og verdipapirer
            </DialogDescription>
          </VisuallyHidden.Root>

          <div className="flex h-full w-full flex-col text-sm max-h-[70svh]">
            <div className="relative pb-3 pr-8">
              <SearchIcon className="size-4 text-muted-foreground absolute left-2 top-2.5" />
              <InputWithDebounce
                className="pl-8"
                withLoader
                value={search}
                onChange={(val) => setSearch(String(val))}
                placeholder="Søk etter kunde eller verdipapir.."
              />
            </div>

            <div className="grid grid-cols-2 gap-6 overflow-hidden flex-1">
              <ContactColumn
                search={search}
                loading={contactsLoading}
                contacts={contacts}
                history={contactHistory.history}
                onClearHistory={contactHistory.clearHistory}
                onRemoveHistoryItem={contactHistory.removeHistoryItem}
                onContactClick={handleContactClick}
                onClose={() => setOpen(false)}
              />
              <SecurityColumn
                search={search}
                loading={securitiesLoading}
                securities={securities}
                history={securityHistory.history}
                onClearHistory={securityHistory.clearHistory}
                onRemoveHistoryItem={securityHistory.removeHistoryItem}
                onSecurityClick={handleSecurityClick}
                onClose={() => setOpen(false)}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface ContactRowProps {
  contact: { id: number; name: string; contactId: string };
  onClick: () => void;
  onRemoveClick?: () => void;
  close?: () => void;
}
const ContactRow = (props: ContactRowProps) => {
  const { contact, onClick, onRemoveClick, close } = props;
  const navigate = useNavigate();

  return (
    <Button
      variant={"ghost"}
      className="group flex items-center font-normal justify-start px-2 py-1.5 h-auto"
      onClick={() => onClick()}
    >
      <div className="flex w-full h-full justify-between items-center">
        <div className={cn("flex items-center w-full gap-2")}>
          <User className="size-8" />
          <div className="flex flex-col items-start justify-start w-full leading-snug">
            <p className="leading-snug font-normal">{contact.name}</p>
            <p className="text-xs text-muted-foreground leading-none">
              {contact.contactId}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-8">
          {onRemoveClick && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onRemoveClick();
              }}
              className="justify-center items-center hidden group-hover:flex p-1 hover:bg-muted-foreground hover:text-muted rounded-full"
            >
              <X className="size-4" />
            </div>
          )}
        </div>
      </div>
    </Button>
  );
};

interface SecurityRowProps {
  security: { id: number; name: string; isin_code: string };
  onClick: () => void;
  onRemoveClick?: () => void;
  close?: () => void;
}
const SecurityRow = (props: SecurityRowProps) => {
  const { security, onClick, onRemoveClick } = props;

  return (
    <Button
      variant={"ghost"}
      className="group flex items-center font-normal justify-start px-2 py-1.5 h-auto"
      onClick={() => onClick()}
    >
      <div className="flex w-full h-full justify-between items-center">
        <div className={cn("flex items-center w-full gap-2")}>
          <LineChart className="size-8" />
          <div className="flex flex-col items-start justify-start w-full leading-snug">
            <p className="leading-snug font-normal">{security.name}</p>
            <p className="text-xs text-muted-foreground leading-none">
              {security.isin_code}
            </p>
          </div>
        </div>
        {onRemoveClick && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onRemoveClick();
            }}
            className="justify-center items-center hidden group-hover:flex p-1 hover:bg-muted-foreground hover:text-muted rounded-full"
          >
            <X className="size-4" />
          </div>
        )}
      </div>
    </Button>
  );
};

export interface SearchItem {
  id: number;
  name: string;
  contactId: string;
}
const saveSearch = (query: SearchItem) => {
  const searches = JSON.parse(localStorage.getItem("searchHistory") || "[]");
  const existingIndex = searches.findIndex(
    (search: SearchItem) => search.id === query.id
  );

  if (existingIndex !== -1) {
    searches.splice(existingIndex, 1);
  }
  searches.unshift(query);
  const updatedSearches = searches.slice(0, 10);
  localStorage.setItem("searchHistory", JSON.stringify(updatedSearches));
};

export const removeSearchHistoryItem = (id: number) => {
  const searches = JSON.parse(localStorage.getItem("searchHistory") || "[]");
  const existingIndex = searches.findIndex(
    (search: SearchItem) => search.id === id
  );

  if (existingIndex !== -1) {
    searches.splice(existingIndex, 1);
  }
  localStorage.setItem("searchHistory", JSON.stringify(searches));
};

export const removeAllExcept = (id: number) => {
  const searches = JSON.parse(localStorage.getItem("searchHistory") || "[]");
  const filteredSearches = searches.filter(
    (search: SearchItem) => search.id === id
  );
  localStorage.setItem("searchHistory", JSON.stringify(filteredSearches));
};

export const getSearchHistory = (): SearchItem[] => {
  return JSON.parse(localStorage.getItem("searchHistory") || "[]");
};

export const deleteSearchHistory = () => {
  localStorage.removeItem("searchHistory");
};

export interface SecuritySearchItem {
  id: number;
  name: string;
  isin_code: string;
}

const saveSecuritySearch = (query: SecuritySearchItem) => {
  const searches = JSON.parse(
    localStorage.getItem("securitySearchHistory") || "[]"
  );
  const existingIndex = searches.findIndex(
    (search: SecuritySearchItem) => search.id === query.id
  );

  if (existingIndex !== -1) {
    searches.splice(existingIndex, 1);
  }
  searches.unshift(query);
  const updatedSearches = searches.slice(0, 10);
  localStorage.setItem(
    "securitySearchHistory",
    JSON.stringify(updatedSearches)
  );
};

export const removeSecuritySearchHistoryItem = (id: number) => {
  const searches = JSON.parse(
    localStorage.getItem("securitySearchHistory") || "[]"
  );
  const existingIndex = searches.findIndex(
    (search: SecuritySearchItem) => search.id === id
  );

  if (existingIndex !== -1) {
    searches.splice(existingIndex, 1);
  }
  localStorage.setItem("securitySearchHistory", JSON.stringify(searches));
};

export const getSecuritySearchHistory = (): SecuritySearchItem[] => {
  return JSON.parse(localStorage.getItem("securitySearchHistory") || "[]");
};

export const deleteSecuritySearchHistory = () => {
  localStorage.removeItem("securitySearchHistory");
};
